import React from 'react';
import { Box, Typography, Paper, Grid, List, ListItem, ListItemIcon, ListItemText, Fade, Zoom } from '@mui/material';
import { colors } from '../../theme/colors';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SlideHeader from './SlideHeader';

const AdditionalInfoSlide: React.FC = () => {
  const benefits = [
    'Professional debt negotiation with creditors',
    'Single monthly program payment',
    'Dedicated account for settlements',
    'Regular progress updates',
    'Online account access',
    'Customer support throughout the program'
  ];

  const disclaimers = [
    'Results may vary based on creditor participation and other factors',
    'Completing the program requires making regular monthly deposits',
    'Creditors are not obligated to accept settlement offers',
    'Program length may vary based on settlement timing and available funds',
    'The program does not fix underlying credit issues'
  ];

  return (
    <Box>
      <SlideHeader title="Additional Information" />
      {/* Content Section */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Grid container spacing={4}>
          <Zoom in={true} style={{ transitionDelay: '100ms' }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                height: '100%',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderLeft: '4px solid #2196f3',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                }
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  color: '#2196f3',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <StarIcon sx={{ fontSize: '1.5rem' }} />
                  Program Benefits
                </Typography>
                <List>
                  {benefits.map((benefit, index) => (
                    <ListItem 
                      key={index}
                      sx={{
                        py: 1,
                        px: 2,
                        mb: 1,
                        bgcolor: 'rgba(33, 150, 243, 0.04)',
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'rgba(33, 150, 243, 0.08)',
                          transform: 'translateX(8px)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleOutlineIcon sx={{ color: '#2196f3' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit}
                        primaryTypographyProps={{
                          sx: { fontWeight: 500 }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Zoom>

          <Zoom in={true} style={{ transitionDelay: '200ms' }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                height: '100%',
                background: 'linear-gradient(135deg, #ffffff 0%, #fff8e1 100%)',
                borderLeft: '4px solid #ed6c02',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                }
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  color: '#ed6c02',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <InfoIcon sx={{ fontSize: '1.5rem' }} />
                  Important Disclaimers
                </Typography>
                <List>
                  {disclaimers.map((disclaimer, index) => (
                    <ListItem 
                      key={index}
                      sx={{
                        py: 1,
                        px: 2,
                        mb: 1,
                        bgcolor: 'rgba(237, 108, 2, 0.04)',
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'rgba(237, 108, 2, 0.08)',
                          transform: 'translateX(8px)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <InfoIcon sx={{ color: '#ed6c02' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={disclaimer}
                        primaryTypographyProps={{
                          sx: { fontWeight: 500 }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Zoom>

          <Fade in={true} style={{ transitionDelay: '300ms' }}>
            <Grid item xs={12}>
              <Paper sx={{ 
                p: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f1f8f1 100%)',
                borderTop: '4px solid #2e7d32',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at top right, rgba(46, 125, 50, 0.03) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  color: '#2e7d32',
                  fontWeight: 600,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '40px',
                    height: '3px',
                    bgcolor: '#2e7d32',
                    borderRadius: '2px'
                  }
                }}>
                  Next Steps
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#2e7d32' }}>
                  If you're ready to move forward, we'll:
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                  {[
                    "Schedule your payment dates",
                    "Review and complete enrollment documents",
                    "Welcome call and Online client portal setup"
                  ].map((step, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 3,
                          height: '100%',
                          bgcolor: 'rgba(46, 125, 50, 0.04)',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s ease-in-out',
                          position: 'relative',
                          '&:hover': {
                            bgcolor: 'rgba(46, 125, 50, 0.08)',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 4px 12px rgba(46, 125, 50, 0.08)'
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 16,
                            bottom: 16,
                            width: 40,
                            height: 40,
                            background: 'radial-gradient(circle, rgba(46, 125, 50, 0.08) 0%, transparent 70%)'
                          }
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start',
                          gap: 2
                        }}>
                          <Typography
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: '#2e7d32',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              flexShrink: 0,
                              fontSize: '1.1rem'
                            }}
                          >
                            {index + 1}
                          </Typography>
                          <Typography 
                            variant="h6"
                            sx={{ 
                              fontWeight: 500,
                              color: '#2e7d32',
                              fontSize: '1.1rem',
                              lineHeight: 1.4
                            }}
                          >
                            {step}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Fade>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdditionalInfoSlide;
